import React from 'react';
import Icon from '../../../components/AppIcon';
import { AnimateOnScroll } from './AnimateOnScroll';

const ProcessSection = () => {
  const steps = [
    {
      number: '01',
      title: 'Discovery & Analysis',
      description: 'In-depth operational audit to align business objectives, technical requirements, and compliance standards.',
      icon: 'Search',
      color: 'text-amber-500',
      bgColor: 'bg-amber-500/10 border-amber-500/20',
    },
    {
      number: '02',
      title: 'Solution Architecture',
      description: 'Custom engineering of automation workflows, AI voice screening parameters, or talent delivery pipelines.',
      icon: 'Layers',
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10 border-blue-500/20',
    },
    {
      number: '03',
      title: 'Rapid Deployment',
      description: 'Accelerated execution with vetted technical experts, automated systems, and dedicated SLA management.',
      icon: 'Zap',
      color: 'text-emerald-500',
      bgColor: 'bg-emerald-500/10 border-emerald-500/20',
    },
    {
      number: '04',
      title: 'Continuous Scaling',
      description: '24/7 operational support, continuous performance tracking, and enterprise scale expansion.',
      icon: 'TrendingUp',
      color: 'text-indigo-500',
      bgColor: 'bg-indigo-500/10 border-indigo-500/20',
    },
  ];

  return (
    <section className="bg-slate-50 dark:bg-slate-900/50 border-b border-border/60 py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <AnimateOnScroll>
          <div className="text-center mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-amber-500/10 border border-amber-500/20 rounded-full mb-3">
              <Icon name="Workflow" size={15} className="text-amber-500" />
              <span className="text-xs md:text-sm font-bold tracking-widest text-amber-600 dark:text-amber-400 uppercase">
                ENTERPRISE ENGAGEMENT PROCESS
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-foreground tracking-tight mb-3">
              How We Partner For Success
            </h2>
            <p className="text-sm md:text-base lg:text-lg text-muted-foreground max-w-2xl mx-auto font-normal">
              A structured 4-step framework engineered for speed, accuracy, and operational excellence.
            </p>
          </div>
        </AnimateOnScroll>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 relative">
          {steps.map((step, index) => (
            <AnimateOnScroll key={step.number} delay={index * 100}>
              <div className="bg-card rounded-2xl p-6 md:p-8 border border-border/80 hover:border-amber-500/40 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1.5 flex flex-col justify-between h-full relative group">
                {/* Step Number Badge */}
                <div className="flex items-center justify-between mb-6">
                  <span className="text-3xl font-black text-amber-500/40 group-hover:text-amber-500 transition-colors font-mono">
                    {step.number}
                  </span>
                  <div className={`w-12 h-12 rounded-xl ${step.bgColor} border flex items-center justify-center group-hover:scale-105 transition-transform`}>
                    <Icon name={step.icon} size={22} className={step.color} />
                  </div>
                </div>

                <div>
                  <h3 className="text-lg md:text-xl font-bold text-foreground mb-2 group-hover:text-primary transition-colors">
                    {step.title}
                  </h3>
                  <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                    {step.description}
                  </p>
                </div>

                {/* Connecting Arrow for Desktop */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute -right-4 top-1/2 -translate-y-1/2 z-10 text-muted-foreground/30">
                    <Icon name="ChevronRight" size={24} />
                  </div>
                )}
              </div>
            </AnimateOnScroll>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
